// client/src/components/pages/DriverPWA/OfflineSync.tsx (converted from .js, ST-00228)
//
// IndexedDB queue for positions the Driver PWA could not send live (offline, or not
// allowed to send). Exposes queueLocation, getQueuedCount and syncQueue via ref.
// Uploading follows offlineQueue.ts (audit TRK-20): only the fixes actually sent are
// removed (the old code cleared the whole store, wiping fixes queued during the
// upload), overlapping syncs share one upload, long queues go in batches, and fixes the
// server rejects as invalid are dropped instead of being retried for ever.
import { useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from 'react';
import api from '../../../services/api';
import type { DriverLocationMessage } from '../../../types/clientPages/driverPWA';
import {
  createQueueSyncer,
  type BatchLocation,
  type BatchSyncResponseBody,
  type LocationQueueStore,
  type QueuedLocation,
} from './offlineQueue';

const DB_NAME = 'LexiBridgeDriverDB';
const STORE_NAME = 'locationQueue';
const DB_VERSION = 1;

/** What OfflineSync exposes on its ref. */
export interface OfflineSyncHandle {
  queueLocation: (locationData: DriverLocationMessage) => Promise<void>;
  getQueuedCount: () => Promise<number>;
  /** Uploads the queue; resolves with the number of fixes the server stored. */
  syncQueue: () => Promise<number>;
}

export interface OfflineSyncProps {
  /** Unused: the server takes the organisation from the session (kept for old callers). */
  tenantId?: number | string;
  onQueueChange?: (count: number) => void;
}

/** Resolves when an IndexedDB request succeeds. */
const requestResult = <T,>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

/** Resolves when a transaction commits. */
const transactionDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

const OfflineSync = forwardRef<OfflineSyncHandle, OfflineSyncProps>(function OfflineSync(
  { onQueueChange },
  ref
) {
  const dbRef = useRef<IDBDatabase | null>(null);

  // Open or create IndexedDB
  const openDB = useCallback((): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      if (dbRef.current) return resolve(dbRef.current);
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        }
      };
      request.onsuccess = () => {
        dbRef.current = request.result;
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }, []);

  const getQueuedCount = useCallback(async (): Promise<number> => {
    try {
      const db = await openDB();
      return await requestResult(
        db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).count()
      );
    } catch {
      return 0;
    }
  }, [openDB]);

  const queueLocation = useCallback(
    async (locationData: DriverLocationMessage): Promise<void> => {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).add({ ...locationData, queuedAt: new Date().toISOString() });
      await transactionDone(tx);
      onQueueChange?.(await getQueuedCount());
    },
    [openDB, onQueueChange, getQueuedCount]
  );

  // The IndexedDB store as the syncer sees it. Built once; the syncer (and its
  // in-flight guard) lives as long as the component.
  const syncerRef = useRef<ReturnType<typeof createQueueSyncer> | null>(null);
  if (!syncerRef.current) {
    const store: LocationQueueStore = {
      getAll: async () => {
        const db = await openDB();
        return requestResult(
          db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
        ) as Promise<QueuedLocation[]>;
      },
      deleteKeys: async (keys) => {
        if (keys.length === 0) return;
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const os = tx.objectStore(STORE_NAME);
        keys.forEach((key) => os.delete(key));
        await transactionDone(tx);
      },
      count: () => getQueuedCount(),
    };
    syncerRef.current = createQueueSyncer(
      store,
      (locations: BatchLocation[]) =>
        api.vehicleLocations.batchSync(locations) as Promise<{ data?: BatchSyncResponseBody }>
    );
  }

  const syncQueue = useCallback(async (): Promise<number> => {
    try {
      const result = await syncerRef.current!.sync();
      onQueueChange?.(result.remaining);
      return result.uploaded;
    } catch (err) {
      console.error('Failed to sync offline queue:', err);
      return 0;
    }
  }, [onQueueChange]);

  // Initialize DB on mount
  useEffect(() => {
    openDB()
      .then(() => getQueuedCount())
      .then((count) => onQueueChange?.(count))
      .catch((err) => console.error('[PWA] Offline queue unavailable:', err));
  }, [openDB, getQueuedCount, onQueueChange]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({ queueLocation, getQueuedCount, syncQueue }), [
    queueLocation,
    getQueuedCount,
    syncQueue,
  ]);

  return null;
});

export default OfflineSync;
